import React, { useMemo, useState } from 'react';
import { Chart, Geom, Axis, Tooltip } from 'bizcharts';
import { Checkbox, Empty } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { SeoScoreProgressChartData } from '../types';

const chartEntriesColors = ['#4BB2C5', '#0A901B', '#E67E22', '#C1380A', '#722D8E', '#33CCC8'];

function getScale(allChartData: SeoScoreProgressChartData[], filteredChartData: SeoScoreProgressChartData[]) {
    let chartTickCount = getTickCount(filteredChartData);
    let maxScore = getMaxAndMinValueFromChartData(allChartData).max;
    let minScore = getMaxAndMinValueFromChartData(allChartData).min;
    let scale: any = {
        score: {
            type: 'linear',
            max: maxScore > 98 ? maxScore : maxScore + 2,
            min: minScore < 2 ? minScore : minScore - 2,
            minTickInterval: 1,
        },
        createdAt: {
            type: 'timeCat',
            mask: 'MMM DD',
            nice: true,
        },
    };

    if (chartTickCount > 1) {
        scale.createdAt.range = [0, 1];
    }
    if (chartTickCount <= 7) {
        scale.createdAt.tickCount = chartTickCount;
    }

    return scale;
}

function getMaxAndMinValueFromChartData(chartData: SeoScoreProgressChartData[]) {
    let range = {
        min: 0,
        max: 0,
    };

    if (chartData.length > 0) {
        let values = chartData.map((dataPoint) => dataPoint.score);
        range.min = Math.min(...values);
        range.max = Math.max(...values);
    }
    return range;
}

function getTickCount(chartData: SeoScoreProgressChartData[]) {
    const dataEntriesCountForUrls: Record<string, number> = {};

    chartData.forEach((dataPoint) => {
        if (!(dataPoint.url in dataEntriesCountForUrls)) {
            dataEntriesCountForUrls[dataPoint.url] = 1;
        } else {
            dataEntriesCountForUrls[dataPoint.url] += 1;
        }
    });

    const arrayOfEntriesCount = Object.values(dataEntriesCountForUrls);

    if (arrayOfEntriesCount.length === 0) {
        return 0;
    }

    return Math.max(...arrayOfEntriesCount);
}

type Props = {
    websiteUrl: string;
    competitors: string[];
    chartData: SeoScoreProgressChartData[];
};

export default function SeoScoreProgressChart({ websiteUrl, competitors, chartData }: Props) {
    const [hiddenCompetitors, setHiddenCompetitors] = useState<string[]>([]);

    const handleCheckboxChange = (event: CheckboxChangeEvent) => {
        const targetCompetitor = event.target.name;
        const isChecked = event.target.checked;

        if (targetCompetitor) {
            if (isChecked) {
                setHiddenCompetitors([...hiddenCompetitors].filter((hiddenCompetitor) => hiddenCompetitor !== targetCompetitor));
            } else {
                setHiddenCompetitors([...hiddenCompetitors, targetCompetitor]);
            }
        }
    };

    const getColorForUrl = (url?: string) => {
        return url === websiteUrl ? chartEntriesColors[0] : chartEntriesColors[1 + competitors.findIndex((competitor) => competitor === url)];
    };

    const competitorsWithData = useMemo(
        () => competitors.filter((competitor) => chartData.some((dataPoint) => dataPoint.url === competitor)),
        [chartData, competitors]
    );
    const hasDataForMainUrl = useMemo(() => chartData.some((dataPoint) => dataPoint.url === websiteUrl), [chartData, websiteUrl]);

    const filteredChartData = useMemo(() => chartData.filter((dataPoint) => !hiddenCompetitors.includes(dataPoint.url)), [chartData, hiddenCompetitors]);

    const scale = useMemo(() => getScale(chartData, filteredChartData), [chartData, filteredChartData]);

    return (
        <div className="seo-score-progress-chart">
            {!hasDataForMainUrl && (
                <Empty
                    className="empty-state"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="There are no reports for the selected interval."
                />
            )}

            {hasDataForMainUrl && (
                <>
                    {competitorsWithData.map((competitor, index) => (
                        <Checkbox
                            className="site-speed-checkbox"
                            style={{
                                // @ts-ignore used in css styles
                                '--background-color': getColorForUrl(competitor),
                                '--border-color': getColorForUrl(competitor),
                            }}
                            key={competitor}
                            name={competitor}
                            checked={!hiddenCompetitors.includes(competitor)}
                            onChange={handleCheckboxChange}
                        >
                            {competitor}
                        </Checkbox>
                    ))}
                    <Chart
                        padding="auto"
                        height={400}
                        data={filteredChartData}
                        scale={scale}
                        animate={false}
                        forceFit
                    >
                        <Axis name="createdAt" />
                        <Axis name="score" />
                        <Tooltip />
                        <Geom
                            type="line"
                            position="createdAt*score"
                            size={2}
                            shape="smooth"
                            color={['url', getColorForUrl]}
                        />
                        <Geom
                            type="point"
                            position="createdAt*score"
                            size={4}
                            shape="circle"
                            style={{ lineWidth: 1 }}
                            color={['url', getColorForUrl]}
                        />
                    </Chart>
                </>
            )}
        </div>
    );
}
